import { SceneObject } from './SceneObject';
import { VideoResource } from './VideoResource';
import { VideoData } from './VideoData';
import { SpriteBatch } from './SpriteBatch';
import { Matrix3 } from './Matrix3';
import { Effect } from './Effect';
import { SceneObjectType } from './SceneObjectType';

export class VideoSceneObject extends SceneObject {
  get runtimeType(): SceneObjectType {
    return SceneObjectType.VideoSprite;
  }
  private _resource: VideoResource;
  private _loop: boolean = false;

  // Per-instance playback. All scene objects of one resource share a single
  // pooled <video>, so simultaneous playbacks restart each other (e.g. icon
  // stop-behavior videos on several reels). Resources whose id matches one of
  // the registered prefixes play a cloned video element instead: each scene
  // object acquires its own instance for the duration of the playback and
  // releases it back to a per-resource pool, so the number of clones stays
  // bounded by the number of concurrent playbacks.
  private static readonly _independentEntries: Array<{
    prefix: string;
    filter?: (v: VideoSceneObject) => boolean;
  }> = [];
  private static readonly _instancePools: Map<string, VideoData[]> = new Map();
  // Every clone ever created - needed to reset their GL textures on context
  // loss (clones are not registered in the resource pools, so the pools'
  // contextLost pass does not reach them).
  private static readonly _allInstances: VideoData[] = [];

  public static contextLost(): void {
    for (const instance of VideoSceneObject._allInstances) {
      instance.reset();
    }
  }

  public static enableIndependentPlayback(
    resourceIdPrefix: string,
    filter?: (video: VideoSceneObject) => boolean
  ): void {
    const existing = VideoSceneObject._independentEntries.find((e) => e.prefix === resourceIdPrefix);
    if (!existing) {
      VideoSceneObject._independentEntries.push({ prefix: resourceIdPrefix, filter });
      return;
    }
    const prev = existing.filter;
    if (!prev || !filter) {
      existing.filter = undefined;
    } else {
      existing.filter = (v) => prev(v) || filter(v);
    }
  }

  private _ownData: VideoData | null = null;

  private get _isIndependent(): boolean {
    const entry = VideoSceneObject._independentEntries.find((e) =>
      this._resource.id.startsWith(e.prefix)
    );
    if (!entry) return false;
    return !entry.filter || entry.filter(this);
  }

  private _acquireOwnData(): VideoData {
    if (!this._ownData) {
      const pool = VideoSceneObject._instancePools.get(this._resource.id);
      this._ownData = pool?.pop() ?? null;
      if (!this._ownData) {
        this._ownData = this._resource.data!.clone();
        VideoSceneObject._allInstances.push(this._ownData);
      }
    }
    return this._ownData;
  }

  private _releaseOwnData(): void {
    if (!this._ownData) {
      return;
    }
    this._ownData.stop();
    let pool = VideoSceneObject._instancePools.get(this._resource.id);
    if (!pool) {
      pool = [];
      VideoSceneObject._instancePools.set(this._resource.id, pool);
    }
    pool.push(this._ownData);
    this._ownData = null;
  }

  set loop(value: boolean) {
    this._loop = value;
  }

  set frame(value: number) {
    if (value === 0) {
      if (this._isIndependent && this._resource.data) {
        this._acquireOwnData().play(this._loop);
      } else {
        this._resource.data!.play(this._loop);
      }
    }
  }

  public prepareToPlay(): Promise<void> {
    return this._resource.data!.prepareToPlay();
  }

  public stop(): void {
    if (this._ownData) {
      this._releaseOwnData();
      return;
    }
    this._resource.data!.stop();
  }

  public pause(): void {
    (this._ownData ?? this._resource.data!).pause();
  }

  public resume(): void {
    (this._ownData ?? this._resource.data!).resume();
  }

  constructor(resource: VideoResource) {
    super();
    this._resource = resource;
    // if(this._resource.data) {
    //   this._resource.data.preinit();
    // }
  }

  public get resourceId(): string {
    return this._resource.id;
  }

  public drawImpl(spriteBatch: SpriteBatch, transform: Matrix3): void {
    // While an own instance is still buffering, fall back to the shared data:
    // for independent resources the shared element never plays, so the
    // fallback is its static first frame.
    const data =
      this._ownData && this._ownData.isCanBePlayed ? this._ownData : this._resource.data!;
    const sprite = data.getSprite();

    if (sprite && sprite.texture) {
      spriteBatch.pushState(Effect.Video);
      spriteBatch.drawRect(sprite.texture, sprite.rect, sprite.rect, transform);
      spriteBatch.popState(Effect.Video);
    }
  }

  public override dispose(): void {
    this._releaseOwnData();
    super.dispose();
  }

  public activeChanged(active: boolean): void {
    if (!active && this._ownData) {
      // The node got hidden (state machine left the playing state) - return
      // the instance to the pool for the next playback.
      this._releaseOwnData();
      if (!this._loop) {
        return;
      }
    }

    if (this._loop) {
      if (active) {
        if (this._isIndependent && this._resource.data) {
          this._acquireOwnData().play(true);
        } else {
          this._resource.data!.play(true);
        }
      } else if (!this._isIndependent) {
        this._resource.data!.stop();
      }
    }
  }
}
